import type { SubmitEvent } from 'react';
import type { Media } from '../../lib/flotiq-client/types/api-response';

import { use, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import Loader from '../../components/Loader/Loader';

import {
  defaultTextRender,
  ModalInstanceContext,
  useModals,
} from '../../contexts/ModalContext';

import {
  CheckedCircleBorderedIcon,
  WarningTriangleRedIcon,
} from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';

export type ReplaceMediaModalFormSubmitFn = (
  file: File,
) => Promise<[boolean, unknown]>;

export interface ReplaceMediaModalContentProps {
  /**
   * Media object to be replaced.
   */
  media: Media;
  /**
   * On form submit.
   */
  onSubmit: ReplaceMediaModalFormSubmitFn;
  /**
   * Count for objects where the media is used.
   */
  usageCount?: number;
  /**
   * Modal test id.
   */
  testId?: string;
}

const getFileExtension = (fileName: string) => {
  const extensionStart = fileName.lastIndexOf('.');
  return extensionStart >= 0
    ? fileName.slice(extensionStart + 1).toLowerCase()
    : '';
};

const ReplaceMediaModalContent = ({
  media,
  usageCount = 0,
  onSubmit,
  testId = '',
}: ReplaceMediaModalContentProps) => {
  const [confirmed, setConfirmed] = useState(usageCount === 0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t } = useTranslation();
  const modal = useModals();
  const modalInstance = use(ModalInstanceContext);

  const { fileName, extension, mimeType } = media;

  const validationSchema = z.object({
    files: z
      .array(
        z
          .file()
          .min(1, t('MediaEdit.ReplaceFile.Errors.EmptyFile'))
          .superRefine((file, ctx) => {
            const fileExtension = getFileExtension(file.name);
            if (fileExtension !== extension.toLowerCase()) {
              ctx.addIssue({
                code: 'custom',
                message: t('MediaEdit.ReplaceFile.Errors.WrongExtension', {
                  extension,
                }),
              });
            } else if (file.type !== mimeType) {
              ctx.addIssue({
                code: 'custom',
                message: t('MediaEdit.ReplaceFile.Errors.WrongMimeType', {
                  mimeType,
                }),
              });
            }
          }),
        t('MediaEdit.ReplaceFile.Errors.NoFile'),
      )
      .min(1, t('MediaEdit.ReplaceFile.Errors.NoFile')),
  });

  const confirmReplacement = async () =>
    await modal({
      title: defaultTextRender.simple(t('MediaEdit.ReplaceFile.Title') + '?'),
      content: (
        <p className="dark:text-slate-200">
          {t('MediaEdit.ReplaceFile.ConfirmationDescription', {
            count: usageCount,
          })}
        </p>
      ),
      buttons: [
        {
          key: 'cancel',
          color: 'blueBordered',
          label: t('Global.Cancel'),
          result: false,
        },
        {
          key: 'replace',
          color: 'blue',
          label: t('MediaEdit.ReplaceFile.Replace'),
          result: true,
        },
      ],
      size: 'lg',
      testId: `${testId}-confirmation-modal`,
    });

  const showStatusModal = (success: boolean) =>
    modal({
      content: (
        <div className="flex flex-col items-center text-center">
          {success ? (
            <CheckedCircleBorderedIcon className="w-14 h-14" />
          ) : (
            <WarningTriangleRedIcon className="w-14 h-14" />
          )}
          <p
            className="font-bold text-3xl mt-4 dark:text-white"
            {...getTestProps(testId, 'status-description')}
          >
            {success
              ? t('MediaEdit.ReplaceFile.Success')
              : t('MediaEdit.ReplaceFile.Error')}
          </p>
        </div>
      ),
      size: 'lg',
      buttons: [
        {
          key: 'ok',
          color: 'blue',
          label: success ? t('Global.Ok') : t('Global.Back'),
          result: true,
        },
      ],
    });

  const form = useAppForm({
    onSubmit: async ({ value }: { value: { files: File[] } }) => {
      const replacementConfirmed = confirmed || (await confirmReplacement());
      if (!replacementConfirmed) return;

      setIsSubmitting(true);
      setConfirmed(true);

      const [success, errors] = await onSubmit(value.files[0]);

      if (success) {
        modalInstance.resolve?.();
        return showStatusModal(true);
      }

      if (errors && Object.keys(errors).length > 0) {
        form.setFieldMeta('files[0]', (prev) => ({
          ...prev,
          errorMap: { onSubmit: Object.values(errors).flat() },
          isTouched: true,
        }));
      } else {
        await showStatusModal(false);
      }

      setIsSubmitting(false);
    },
    validators: {
      onChange: validationSchema,
      onSubmit: validationSchema,
    },
  });

  const onFormSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    form.handleSubmit();
  };

  const handleDrop = (files: File[]) => {
    form.setFieldValue('files', [files?.[0]]);
  };

  return (
    <div className="w-full h-full" {...getTestProps(testId, 'content')}>
      {isSubmitting && (
        <div className="absolute inset-0 bg-white/50 dark:bg-slate-900/50 z-50 flex items-center justify-center">
          <Loader size="normal" type="spinner-grid" />
        </div>
      )}

      <p>
        {t('MediaEdit.ReplaceFile.Description', {
          fileName,
          extension,
        })}
      </p>
      <form.AppForm>
        <form onSubmit={onFormSubmit} noValidate>
          <div className="mt-6 mb-2">
            <form.AppField name="files">
              {(field) => (
                <field.FilesSelectorField
                  testId={testId}
                  dropFileHandler={handleDrop}
                  multiple={false}
                  dndLabel={t('MediaEdit.ReplaceFile.AcceptedExtension', {
                    extension,
                  })}
                  accept={mimeType}
                  disabled={isSubmitting}
                />
              )}
            </form.AppField>
          </div>
          <div
            className="absolute w-full left-0 bottom-0 p-3 flex justify-center items-center gap-2 bg-white
          border-t dark:bg-slate-900 dark:border-slate-700 rounded-b-md"
          >
            <Button
              buttonColor="blueBordered"
              onClick={() => modalInstance.resolve?.()}
              disabled={isSubmitting}
              {...getTestProps(testId, 'cancel-button', 'testId')}
            >
              {t('Global.Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              {...getTestProps(testId, 'replace-button', 'testId')}
            >
              {t('MediaEdit.ReplaceFile.Replace')}
            </Button>
          </div>
        </form>
      </form.AppForm>
    </div>
  );
};

export default ReplaceMediaModalContent;
